import { ExtendFile } from '@/components/file-thumbnail/utils';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';

export const getDefaultDisplayTime = () =>
  dayjs().hour(9).minute(0).second(0).millisecond(0).toDate();

export enum BooleanOption {
  TRUE = 'true',
  FALSE = 'false',
}

export type InitialBoardFormData = {
  subject: string;
  categories: string[];
  content: string;
  useImageSlides: boolean;
  imageSlides: ExtendFile[];
  attachments: ExtendFile[];
  useUploadTime: BooleanOption;
  displayTime: null | Date;
  displayStartedAt: null | Date;
  isImportant: BooleanOption;
  commentPosition: 'footerSection' | 'rightSection';
  displaySections:
    | 'bannerSection'
    | 'rightFixSection'
    | 'rightSection'
    | 'importantSection'
    | 'interviewSection'
    | 'none';
  useThumbnail: BooleanOption;
  /** 목록 카드에 제목을 노출할지 여부. BE 의 `config.isListSubjectVisible` 와 짝을 이룬다. */
  showTitleInList: BooleanOption;
  thumbnailFile: ExtendFile | null;
  useThumbnailDisplayTime: BooleanOption;
  thumbnailDisplayStartedAt: null | Date;
  thumbnailDisplayEndAt: null | Date;
  // 코드 업로드 관련 필드
  formType: 'editor' | 'codeUpload';
  codeZipFile: ExtendFile | null;
  axLoungeDeploy: {
    fileUuid: string;
    version: string;
  };
};

/** 제목 표시 여부를 고를 수 있는 노출 위치. 메인 배너와 우측 상단 고정은 제목을 그리지 않는다. */
const TITLE_OPTION_SECTIONS: InitialBoardFormData['displaySections'][] = [
  'rightSection',
  'none',
];

type TitleOptionValues = Pick<
  InitialBoardFormData,
  'displaySections' | 'useThumbnail' | 'showTitleInList'
>;

/**
 * 제목 표시 여부를 고를 수 있는지.
 * 썸네일마저 없으면 카드에 아무것도 남지 않으므로, 썸네일을 쓸 때만 고를 수 있다.
 */
export const canHideTitleInList = ({
  displaySections,
  useThumbnail,
}: Omit<TitleOptionValues, 'showTitleInList'>) =>
  useThumbnail === BooleanOption.TRUE &&
  TITLE_OPTION_SECTIONS.includes(displaySections);

/** 목록 카드에 제목이 보이는지. 고를 수 없는 상태면 남아 있는 선택과 무관하게 보인다. */
export const isTitleShownInList = (values: TitleOptionValues) =>
  !canHideTitleInList(values) || values.showTitleInList !== BooleanOption.FALSE;

export type BoardFormData = InitialBoardFormData & {
  id: string;
  createdTime: Date;
  updatedTime: Date;
};

export const defaultValues: InitialBoardFormData = {
  subject: '',
  content: '',
  categories: [],
  useImageSlides: false,
  imageSlides: [],
  attachments: [],
  useUploadTime: BooleanOption.FALSE,
  displayTime: getDefaultDisplayTime(),
  displayStartedAt: null,
  isImportant: BooleanOption.FALSE,
  commentPosition: 'footerSection',
  displaySections: 'none',
  useThumbnail: BooleanOption.FALSE,
  showTitleInList: BooleanOption.TRUE,
  thumbnailFile: null,
  useThumbnailDisplayTime: BooleanOption.FALSE,
  thumbnailDisplayStartedAt: null,
  thumbnailDisplayEndAt: null,
  formType: 'editor',
  codeZipFile: null,
  axLoungeDeploy: {
    fileUuid: '',
    version: '1.0.0',
  },
};

const ExtendFileScheme = {
  key: Yup.string(),
  name: Yup.string(),
  path: Yup.string().optional(),
  preview: Yup.string().optional(),
  status: Yup.string().oneOf(['new', 'deleted', 'default']),
  type: Yup.string().optional(),
  isUploaded: Yup.boolean().optional(),
  uuid: Yup.string().optional(),
};

const UploadedCodeFileScheme = Yup.object()
  .shape(ExtendFileScheme)
  .test(
    'is-uploaded-code-file',
    '파일 업로드가 완료될 때까지 기다려주세요.',
    (file) =>
      Boolean(
        file && file.isUploaded && file.uuid && (file.path || file.preview),
      ),
  );

const Schema = Yup.object().shape({
  id: Yup.string().optional(),
  formType: Yup.string().oneOf(['editor', 'codeUpload']).required(),
  subject: Yup.string()
    .required('제목을 입력해주세요.')
    .transform((value) => value.trim())
    .max(100, '제목은 최대 100자까지 입력 가능합니다.'),
  codeZipFile: Yup.object()
    .shape(ExtendFileScheme)
    .nullable()
    .when('formType', {
      is: (value: string) => value === 'codeUpload',
      then: () =>
        UploadedCodeFileScheme.required('결과물 압축 파일을 업로드해주세요.'),
      otherwise: (schema) => schema.nullable(),
    }),
  categories: Yup.array().of(Yup.string()).min(1, '카테고리를 선택해주세요.'),
  content: Yup.string().optional(),
  imageSlides: Yup.array()
    .of(Yup.object().shape(ExtendFileScheme))
    .when('useImageSlides', {
      is: (value: string) => value,
      then: (value) => value.min(1, '이미지를 선택해주세요.'),
      otherwise: (value) => value.min(0),
    }),
  attachments: Yup.array().of(Yup.object().shape(ExtendFileScheme)),
  displayTime: Yup.date()
    .nullable()
    .when('useUploadTime', {
      is: (value: string) => value === BooleanOption.TRUE,
      then: (displayTime) => displayTime.required(),
      otherwise: (displayTime) => displayTime.nullable(),
    }),
  isImportant: Yup.string().oneOf([BooleanOption.TRUE, BooleanOption.FALSE]),
  commentPosition: Yup.string()
    .oneOf(['footerSection', 'rightSection'])
    .required(),
  displaySections: Yup.string()
    .oneOf([
      'importantSection',
      'interviewSection',
      'bannerSection',
      'rightFixSection',
      'rightSection',
      'none',
    ])
    .required(),
  showTitleInList: Yup.string().oneOf([
    BooleanOption.TRUE,
    BooleanOption.FALSE,
  ]),
  thumbnailFile: Yup.object()
    .shape(ExtendFileScheme)
    .nullable()
    .when('useThumbnail', {
      is: (value: BooleanOption) => value === BooleanOption.TRUE,
      then: (value) => value.required(),
      otherwise: (value) => value.nullable(),
    }),
  thumbnailDisplayStartedAt: Yup.date()
    .nullable()
    .when('useThumbnailDisplayTime', {
      is: (value: BooleanOption) => value === BooleanOption.TRUE,
      then: (schema) =>
        schema
          .required('시작일을 입력해주세요.')
          .test(
            'is-before-end',
            '종료일이 시작일보다 빠르게 설정되었어요.',
            function (value, context) {
              const {
                parent: { thumbnailDisplayEndAt },
              } = context;
              return (
                !value ||
                !thumbnailDisplayEndAt ||
                value <= thumbnailDisplayEndAt
              );
            },
          ),
      otherwise: (schema) => schema.nullable(),
    }),

  thumbnailDisplayEndAt: Yup.date()
    .nullable()
    .when('useThumbnailDisplayTime', {
      is: (value: BooleanOption) => value === BooleanOption.TRUE,
      then: (schema) =>
        schema
          .required('종료일을 입력해주세요.')
          .test('is-after-start', '', function (value, context) {
            const {
              parent: { thumbnailDisplayStartedAt },
            } = context;

            return (
              !value ||
              !thumbnailDisplayStartedAt ||
              value >= thumbnailDisplayStartedAt
            );
          }),
      otherwise: (schema) => schema.nullable(),
    }),
});

const useBoardForm = () => {
  return useForm<BoardFormData>({
    resolver: yupResolver(Schema),
    defaultValues,
    mode: 'onChange',
  });
};

export default useBoardForm;
